/**
 * Turns any picture the merchant picks into a file the backend can serve.
 *
 * The upload endpoints (store profile, collection images, product images) accept anything and
 * store it in S3 with a sniffed Content-Type (Go's http.DetectContentType). A HEIC photo from an
 * iPhone becomes `application/octet-stream`, which browsers can't show in the store. So:
 * - No picture is rejected for its weight or its dimensions: we prepare it here instead.
 * - A JPG / PNG / WEBP is kept as it is only when it weighs at most TARGET_BYTES AND its longest
 *   side is at most MAX_SIDE px (safe for S3 sniffing and every browser).
 * - Everything else is redrawn to at most MAX_SIDE px (EXIF rotation applied): AVIF, HEIC/HEIF,
 *   GIF, BMP, TIFF, SVG, files without a type or extension, and heavy or huge pictures. No file is
 *   rejected by its name: if the browser can decode it, it gets converted.
 *   - Sources that can be transparent (PNG, WEBP, GIF, SVG) are tried as PNG first so logos keep
 *     their transparency; if that PNG weighs more than TARGET_BYTES we fall back to JPEG.
 *   - JPEG (transparency flattened on white) goes down SHRINK_STEPS (lower quality, then smaller
 *     sides) until it fits TARGET_BYTES. After the last step we keep whatever we got.
 * - Huge pictures are decoded already downscaled with `createImageBitmap` (resize options,
 *   `imageOrientation: 'from-image'`) where the browser supports it, so the full-size bitmap never
 *   has to live in memory; otherwise we draw from a plain `Image`.
 * - HEIC/HEIF that the browser can't decode (Chrome/Firefox desktop) is converted with heic-to
 *   (maintained, recent libheif: handles photos from current iPhones), loaded only when needed
 *   (separate chunk), and then goes through the same redraw.
 * - ImageNotSupportedError is only thrown for files we can't decode at all (not an image,
 *   corrupted, HEIC that heic-to can't convert).
 */

export const ACCEPT_ANY_IMAGE = 'image/*,.heic,.heif';

const KEEP_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
/** Sources that may carry transparency: redrawn as PNG first. */
const ALPHA_TYPES = ['image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
/** Max weight of what we upload (kept files and redraws alike). */
const TARGET_BYTES = 3 * 1024 * 1024;
const MAX_SIDE = 2000;
const JPEG_QUALITY = 0.88;
/** JPEG attempts, in order, until the result fits TARGET_BYTES. */
const SHRINK_STEPS = [
    { side: MAX_SIDE, quality: JPEG_QUALITY },
    { side: MAX_SIDE, quality: 0.75 },
    { side: 1600, quality: 0.7 },
    { side: 1200, quality: 0.6 },
];

export class ImageNotSupportedError extends Error {
    constructor(message = 'image not supported') {
        super(message);
        this.name = 'ImageNotSupportedError';
    }
}

export const isHeic = (file) =>
    /image\/hei[cf]/i.test((file && file.type) || '') || /\.(heic|heif)$/i.test((file && file.name) || '');

const HEIF_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'];

/** HEIC/HEIF by content (`....ftypheic`), for files that arrive without a type or extension. */
const sniffHeic = async (file) => {
    try {
        const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
        const box = String.fromCharCode(...head.slice(4, 8));
        const brand = String.fromCharCode(...head.slice(8, 12));
        return box === 'ftyp' && HEIF_BRANDS.includes(brand.toLowerCase());
    } catch {
        return false;
    }
};

/** Clearly not a picture: skip the decode attempt and fail fast. */
const isClearlyNotImage = (file) =>
    /^(text|audio|video|font)\//i.test(file.type || '') ||
    /^application\/(pdf|zip|json|msword|vnd\.)/i.test(file.type || '');

const baseName = (name) => String(name || 'foto').replace(/\.[^.]+$/, '') || 'foto';

/** Scale factor that brings the longest side down to `side` (never upscales). */
const fitScale = (width, height, side) => Math.min(1, side / Math.max(width, height));

/**
 * Loads the picture into an `Image`. Browsers only read the header here (the pixels are decoded
 * when drawn), so this is also a cheap way to know the dimensions (EXIF orientation applied).
 */
const decode = (blob) =>
    new Promise((resolve, reject) => {
        const url = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => resolve({ img, url });
        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new ImageNotSupportedError('decode failed'));
        };
        img.src = url;
    });

/** True when the picture can be uploaded as it is: decodable and longest side <= MAX_SIDE. */
const fitsMaxSide = async (blob) => {
    try {
        const { img, url } = await decode(blob);
        URL.revokeObjectURL(url);
        const longest = Math.max(img.naturalWidth, img.naturalHeight);
        return longest > 0 && longest <= MAX_SIDE;
    } catch {
        return false;
    }
};

/**
 * Decodes and downscales in one go, so a huge picture never sits in memory at full size.
 * Only the longest side is given: the browser keeps the aspect ratio whether it applies the
 * resize before or after the EXIF rotation. Returns null when unsupported or failing.
 */
const decodeDownscaled = async (blob, width, height) => {
    if (typeof createImageBitmap !== 'function') return null;
    const scale = fitScale(width, height, MAX_SIDE);
    const resize =
        width >= height
            ? { resizeWidth: Math.round(width * scale) }
            : { resizeHeight: Math.round(height * scale) };
    try {
        const bitmap = await createImageBitmap(blob, {
            ...resize,
            resizeQuality: 'high',
            imageOrientation: 'from-image',
        });
        return bitmap && bitmap.width && bitmap.height ? bitmap : null;
    } catch {
        return null; // e.g. SVG, or a browser that doesn't know these options
    }
};

/**
 * Something drawable plus its dimensions: an ImageBitmap already at <= MAX_SIDE when the source is
 * bigger and the browser can do it, the `Image` otherwise. Call `release()` when done.
 */
const loadSource = async (blob) => {
    const { img, url } = await decode(blob);
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (!width || !height) {
        URL.revokeObjectURL(url);
        throw new ImageNotSupportedError('empty image');
    }
    const bitmap =
        fitScale(width, height, MAX_SIDE) < 1 ? await decodeDownscaled(blob, width, height) : null;
    if (bitmap) {
        URL.revokeObjectURL(url);
        return {
            source: bitmap,
            width: bitmap.width,
            height: bitmap.height,
            release: () => bitmap.close && bitmap.close(),
        };
    }
    return { source: img, width, height, release: () => URL.revokeObjectURL(url) };
};

/** Draws the source with its longest side at most `side` and encodes it. Frees the canvas after. */
const render = async ({ source, width, height }, { side, type, quality, background }) => {
    const scale = fitScale(width, height, side);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext('2d');
    if (background) {
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
    const out = await new Promise((resolve) => canvas.toBlob(resolve, type, quality));
    canvas.width = 0; // iOS Safari caps the total canvas memory: release it right away
    canvas.height = 0;
    return out;
};

const toFile = (blob, name, ext, type) => {
    const fileName = `${baseName(name)}.${ext}`;
    try {
        return new File([blob], fileName, { type });
    } catch {
        blob.name = fileName; // old Safari without the File constructor
        return blob;
    }
};

/**
 * Redraws the picture within MAX_SIDE and TARGET_BYTES: PNG first when it may be transparent,
 * then JPEG going down SHRINK_STEPS. After the last step the last JPEG is returned as it is.
 */
const redraw = async (blob, name, tryPng) => {
    const loaded = await loadSource(blob);
    try {
        if (tryPng) {
            const png = await render(loaded, { side: MAX_SIDE, type: 'image/png' });
            if (png && png.type === 'image/png' && png.size <= TARGET_BYTES) {
                return toFile(png, name, 'png', 'image/png');
            }
        }
        let jpeg = null;
        for (const { side, quality } of SHRINK_STEPS) {
            // JPEG has no alpha: flatten transparency on white
            const out = await render(loaded, {
                side,
                type: 'image/jpeg',
                quality,
                background: '#FFFFFF',
            });
            if (out) jpeg = out;
            if (jpeg && jpeg.size <= TARGET_BYTES) break;
        }
        if (!jpeg) throw new ImageNotSupportedError('encode failed');
        return toFile(jpeg, name, 'jpg', 'image/jpeg');
    } finally {
        loaded.release();
    }
};

/**
 * @param {File} file what the input / drop gave us, of any weight or dimensions
 * @returns {Promise<File>} a JPG/PNG/WEBP file ready to upload (<= MAX_SIDE px, ~<= TARGET_BYTES)
 * @throws {ImageNotSupportedError} when it isn't an image we can read
 */
export const normalizeImageFile = async (file) => {
    if (!file || isClearlyNotImage(file)) throw new ImageNotSupportedError('not an image');
    const keepable = KEEP_TYPES.includes(file.type) && file.size <= TARGET_BYTES;
    if (keepable && (await fitsMaxSide(file))) return file;

    try {
        return await redraw(file, file.name, ALPHA_TYPES.includes(file.type));
    } catch (error) {
        if (!(isHeic(file) || (await sniffHeic(file)))) throw error;
    }

    let heicTo;
    try {
        ({ heicTo } = await import(/* webpackChunkName: "heic-to" */ 'heic-to'));
    } catch {
        throw new ImageNotSupportedError('heic converter unavailable');
    }
    let jpeg;
    try {
        jpeg = await heicTo({ blob: file, type: 'image/jpeg', quality: JPEG_QUALITY });
    } catch {
        throw new ImageNotSupportedError('heic conversion failed');
    }
    return redraw(jpeg, file.name, false);
};
